import { ParamListBase } from "@react-navigation/core"
import { getPathFromState, LinkingOptions, NavigationContainer } from "@react-navigation/native"
import { NavigationState } from "@react-navigation/routers"
import { AlertProvider, logger } from "capsule"
import React, { useCallback, useEffect, useRef } from "react"
import { LogBox } from "react-native"

import { auth, firestore } from "./capsule/features/Firebase/firebase"
import { BOTTOM_SIZE } from "./features/config/Constants"
import { isE2EEnvironment } from "./features/config/environment"
import { ignoreWarnings } from "./features/config/LogBox"
import { PREPROD_PROJECT_ID, PROD_PROJECT_ID } from "./features/config/projectIds"
import { setMockedDate } from "./features/hooks/useDateMock"
import { navigationRef } from "./features/Navigation/NavigationService"
import RootNavigator, { screens } from "./features/Navigation/RootNavigator"
import PromsProvider from "./features/Providers/PromsProvider"
import RootProvider from "./features/Providers/RootProvider"
import ScreenTracking from "./services/Analytics/analytics"
import crashReporter from "./services/Analytics/crashlytics"
// import shake from "./services/Analytics/shake"
import ThemeApp from "./ThemeApp"
import LoadFonts from "./utils/loadFonts"

const linking: LinkingOptions<ParamListBase> = {
  prefixes: [
    "https://app.doctup.fr",
    "http://localhost:3000",
    `https://${PROD_PROJECT_ID}.web.app`,
    `https://${PROD_PROJECT_ID}.firebaseapp.com`,
    `https://${PREPROD_PROJECT_ID}.web.app`,
    `https://${PREPROD_PROJECT_ID}.firebaseapp.com`,
  ],
  config: { screens },
  getPathFromState: (state, options) => (state == null ? "/" : getPathFromState(state, options)),
}

ignoreWarnings()
// shake()

// Use the following when testing with a local instance of firebase and firestore
const USE_EMULATOR = __DEV__ && isE2EEnvironment()

if (USE_EMULATOR) {
  // Connect to Firebase emulators
  // Note: Functions emulator is configured in firebase.ts
  firestore().settings({
    host: "localhost:8080",
    ssl: false,
    persistence: false,
    cacheSizeBytes: firestore.CACHE_SIZE_UNLIMITED,
  })

  // Auth emulator connection
  // Note: React Native Firebase v22+ auto-connects to auth emulator via FIREBASE_AUTH_EMULATOR_HOST
  // but we can verify it here
  auth().useEmulator("http://localhost:9099")
  LogBox.ignoreAllLogs(true)

  // Set a fixed date for E2E tests: April 24, 2026 at 12:00:00 UTC
  // This ensures tests are reproducible and not affected by the current date
  setMockedDate(new Date("2026-04-24T12:00:00.000Z").getTime()) // 2026-04-24T12:00:00.000Z
}

const App = () => {
  useEffect(() => {
    crashReporter.ensureBugsnagStarted()
  }, [])
  const first = useRef(true)
  const routeNameRef = useRef<string | Partial<NavigationState> | undefined>()

  // useDateMock()

  const screenTracking = useCallback((navigationState: NavigationState | undefined) => {
    const getActiveRoute = (innerState: NavigationState | undefined) => {
      const route = innerState?.routes[innerState?.index || 0]
      if (route?.state) {
        return getActiveRoute(route.state as NavigationState)
      }
      return route
    }

    const activeRoute = getActiveRoute(navigationState)
    const currentRouteName = activeRoute?.name
    const routeParams = activeRoute?.params

    // Logique existante pour le suivi des écrans
    const previousRouteName = routeNameRef.current
    if (first.current || previousRouteName !== currentRouteName) {
      first.current = false
      ScreenTracking.logScreenTracking(currentRouteName)
    }
    // Suivi spécifique pour les articles de Faq
    if (currentRouteName === "Faq_Article" && routeParams?.id) {
      ScreenTracking.logScreenTracking(`${currentRouteName}_${routeParams.id}`)
    }

    // Sauvegarder le nom de la route actuelle pour une comparaison ultérieure
    routeNameRef.current = currentRouteName
  }, [])

  useEffect(() => {
    // @ts-ignore
    const state = navigationRef?.current?.getRootState()
    // Save the initial route name
    routeNameRef.current = ScreenTracking.getActiveRouteName(state)
  }, [])

  logger("reload App")

  // @ts-ignore
  return (
    <NavigationContainer
      // @ts-ignore
      ref={navigationRef}
      onStateChange={screenTracking}
      linking={linking}
    >
      <RootProvider>
        <LoadFonts
          fonts={{
            Montserrat: [
              { file: "Montserrat-Regular", weight: 400 },
              { file: "Montserrat-Medium", weight: 600 },
              { file: "Montserrat-Bold", weight: 700 },
            ],
          }}
        />
        <ThemeApp>
          <AlertProvider hasBottomNav bottomHeight={BOTTOM_SIZE}>
            <PromsProvider>
              <RootNavigator />
            </PromsProvider>
          </AlertProvider>
        </ThemeApp>
      </RootProvider>
    </NavigationContainer>
  )
}

export default App
